import { isDymoServiceOrigin } from '@seller/shared';
import { DYMO_30336, DYMO_JOB_MODE, buildDymoJob, type MeasureText } from './dymo-label.js';
import {
  DymoServiceError,
  PROBE_TIMEOUT_MS,
  findService,
  getConsumable,
  getPrinters,
  printLabel,
  type DymoConsumable,
  type DymoPrinter,
  type FetchLike,
} from './dymo-service.js';
import type { LabelJobItem } from './pdf.js';

/**
 * What this browser can see of a DYMO label printer, and the one function that
 * prints to it. Everything the outside world supplies — `fetch`, the two
 * remembered values, the browser's permission state, the text measurer — comes
 * in through {@link DymoEnvironment}, so the whole flow is tested in Node
 * against a mocked service (dymo-browser.ts supplies the real ones).
 *
 * Detection answers, in order: does DYMO Connect's service answer on this
 * computer; which printers does it list; which one does this browser use; is
 * that one connected; and (LabelWriter 550 series only) which roll does it hold.
 * Each way of that going wrong is its own state with its own sentence.
 */

export type DymoState =
  /** Nothing answered on any of the service's ports. */
  | 'no_service'
  /** The browser is set to block this page from reaching software on this computer. */
  | 'blocked'
  /** The service answers but lists no LabelWriter printer. */
  | 'no_printer'
  /** The chosen LabelWriter is listed but not connected (unplugged, off, or off the network). */
  | 'offline'
  /** A connected LabelWriter is ready to take a job. */
  | 'ready';

export interface DymoStatus {
  state: DymoState;
  /** The service's origin when it answered. */
  origin: string | null;
  /** Every printer DYMO Connect lists, LabelWriter or not, connected or not. */
  printers: DymoPrinter[];
  /** The LabelWriter this browser prints to; null when there is none. */
  printer: DymoPrinter | null;
  /** The roll the printer reports (550 series); null when unknown. */
  consumable: DymoConsumable | null;
}

/** What detection and printing need from outside. */
export interface DymoEnvironment {
  fetch: FetchLike;
  /** The service origin that answered last time, and where to keep the next one. */
  getRememberedOrigin: () => string | null;
  setRememberedOrigin: (origin: string | null) => void;
  /** The printer name this browser chose on the settings page. */
  getChosenPrinter: () => string | null;
  /**
   * The browser's permission for this page to reach loopback addresses, when the
   * browser can say: `denied` skips the probe, `prompt` waits longer for it (the
   * requests are held while the browser's question is on screen). Null when the
   * browser has no such permission.
   */
  loopbackPermission?: () => Promise<'granted' | 'denied' | 'prompt' | null>;
  /**
   * Told when the probe is about to start while the permission is `prompt`: from
   * here the browser may be holding the requests behind its question, for as
   * long as {@link PERMISSION_PROMPT_TIMEOUT_MS} per host. The page uses it to
   * say so and to offer another way to print meanwhile (dymo-browser.ts).
   */
  onPermissionPrompt?: () => void;
  /** Measures the title in points for the middle cut. */
  measureTitle: MeasureText;
  /** How long the print call may take; the client's default when absent. */
  printTimeoutMs?: number;
}

/** How long the probe waits when the browser may be asking the user for permission. */
export const PERMISSION_PROMPT_TIMEOUT_MS = 45_000;

/**
 * How long a check may sit behind the browser's permission question before the
 * page stops making the user wait for it: the check carries on, but the page
 * says what the browser is waiting for and lets the list be printed as a PDF.
 */
export const PERMISSION_WAIT_NOTICE_MS = 10_000;

/** What the page says once a check has waited that long behind the browser's question. */
export const PERMISSION_WAIT_MESSAGE =
  'The browser is waiting for you to allow this page to connect to DYMO Connect on this computer. Look for its question near the address bar (it asks about apps on this device) and choose Allow.';

/** Whether a printer can print these labels at all: only a LabelWriter takes die-cut rolls. */
export function isLabelWriter(printer: DymoPrinter): boolean {
  return printer.type === 'LabelWriterPrinter';
}

/**
 * The printer this browser prints to: the one chosen on the settings page while
 * DYMO Connect still lists it as a LabelWriter; otherwise the first connected
 * LabelWriter; otherwise the first LabelWriter. Tape and other printers are
 * never chosen. Null when there is no LabelWriter.
 */
export function choosePrinter(
  printers: readonly DymoPrinter[],
  chosenName: string | null,
): DymoPrinter | null {
  const labelWriters = printers.filter(isLabelWriter);
  return (
    labelWriters.find((p) => p.name === chosenName) ??
    labelWriters.find((p) => p.isConnected) ??
    labelWriters[0] ??
    null
  );
}

async function permissionOf(env: DymoEnvironment): Promise<'granted' | 'denied' | 'prompt' | null> {
  try {
    return (await env.loopbackPermission?.()) ?? null;
  } catch {
    return null;
  }
}

/** A status with nothing found, for the two states where the service never answered. */
export function nothingFound(state: 'blocked' | 'no_service'): DymoStatus {
  return { state, origin: null, printers: [], printer: null, consumable: null };
}

/** Asks DYMO Connect what this computer can print to. Never throws. */
export async function detectDymo(env: DymoEnvironment): Promise<DymoStatus> {
  const permission = await permissionOf(env);
  if (permission === 'denied') return nothingFound('blocked');

  if (permission === 'prompt') {
    try {
      env.onPermissionPrompt?.();
    } catch {
      // A listener's failure is not a reason to stop looking for the printer.
    }
  }

  const remembered = env.getRememberedOrigin();
  const origin = await findService({
    fetch: env.fetch,
    remembered,
    timeoutMs: permission === 'prompt' ? PERMISSION_PROMPT_TIMEOUT_MS : PROBE_TIMEOUT_MS,
  });
  if (origin !== remembered) env.setRememberedOrigin(origin);
  if (origin === null) {
    // The question may have been answered "Block" while the probe waited.
    const after = permission === 'prompt' ? await permissionOf(env) : permission;
    return nothingFound(after === 'denied' ? 'blocked' : 'no_service');
  }

  let printers: DymoPrinter[];
  try {
    printers = await getPrinters(origin, env.fetch);
  } catch {
    // It answered the status probe and then failed: treated as not usable, with
    // the origin kept so the message can say the service was found.
    return { state: 'no_printer', origin, printers: [], printer: null, consumable: null };
  }
  const printer = choosePrinter(printers, env.getChosenPrinter());
  if (printer === null) {
    return { state: 'no_printer', origin, printers, printer: null, consumable: null };
  }
  if (!printer.isConnected) {
    return { state: 'offline', origin, printers, printer, consumable: null };
  }
  const consumable = await getConsumable(origin, printer.name, env.fetch);
  return { state: 'ready', origin, printers, printer, consumable };
}

/** Why the DYMO printer cannot be used, in a sentence; null when it can. */
export function unavailableReason(status: DymoStatus): string | null {
  switch (status.state) {
    case 'ready':
      return null;
    case 'blocked':
      return 'This browser is set to block this page from reaching DYMO Connect on this computer. Allow it in the site settings (the icon left of the address), then check again.';
    case 'no_service':
      return 'DYMO Connect was not found on this computer. Install it (or start it), then check again.';
    case 'no_printer':
      return status.printers.length > 0
        ? 'DYMO Connect is running, but none of its printers is a LabelWriter.'
        : 'DYMO Connect is running, but it has no printer. Plug the LabelWriter in, or add it in DYMO Connect with File > Add Network Printer, then check again.';
    case 'offline':
      return `${status.printer?.name ?? 'The LabelWriter'} is not connected. Check that it is switched on and plugged in (or on the network), then check again.`;
  }
}

/** Whether a reported roll is the 30336 these labels are laid out for. */
export function isExpectedRoll(consumable: DymoConsumable): boolean {
  const wanted = DYMO_30336.sku;
  return [consumable.sku, consumable.name].some((text) => text !== null && text.includes(wanted));
}

/**
 * What is worth saying about the roll before a print, as sentences. Warnings
 * only: the printer's report is shown, never trusted enough to refuse a print.
 * `labels` is the size of the job about to be sent, when there is one.
 */
export function rollWarnings(consumable: DymoConsumable | null, labels?: number): string[] {
  if (consumable === null) return [];
  const warnings: string[] = [];
  const identified = consumable.sku !== null || consumable.name !== null;
  if (identified && !isExpectedRoll(consumable)) {
    const roll = [consumable.sku, consumable.name].filter((t) => t !== null).join(', ');
    warnings.push(
      `The printer reports a different roll (${roll}). These labels are laid out for DYMO 30336, 1 x 2-1/8 in.`,
    );
  }
  const remaining = consumable.labelsRemaining;
  if (remaining === 0) {
    warnings.push('The printer reports no labels left on the roll.');
  } else if (remaining !== null && labels !== undefined && remaining < labels) {
    warnings.push(
      `The printer reports ${remaining} ${remaining === 1 ? 'label' : 'labels'} left on the roll, fewer than the ${labels} in this job.`,
    );
  }
  return warnings;
}

/** The roll in a few words for the status line; null when the printer does not report one. */
export function rollSummary(consumable: DymoConsumable | null): string | null {
  if (consumable === null) return null;
  const parts: string[] = [];
  const roll = consumable.sku ?? consumable.name;
  if (roll !== null) parts.push(`roll ${roll}`);
  if (consumable.labelsRemaining !== null) {
    parts.push(
      `${consumable.labelsRemaining} ${consumable.labelsRemaining === 1 ? 'label' : 'labels'} left`,
    );
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

// ---------------------------------------------------------------------------
// Printing
// ---------------------------------------------------------------------------

/** A print that did not (or may not have) come out; the message is for the page. */
export class DymoPrintError extends Error {
  /** Labels in calls the service accepted before the failure. */
  readonly labelsSent: number;
  /** True when labels may have printed although the call failed (a timeout). */
  readonly uncertain: boolean;

  constructor(message: string, options: { labelsSent?: number; uncertain?: boolean } = {}) {
    super(message);
    this.name = 'DymoPrintError';
    this.labelsSent = options.labelsSent ?? 0;
    this.uncertain = options.uncertain ?? false;
  }
}

export interface DymoPrintResult {
  printerName: string;
  /** Labels handed to the printer. */
  labels: number;
  /** How many print jobs that took: one, unless the per-label mode is on. */
  jobs: number;
  /** What the printer reported about its roll that the user should know. */
  warnings: string[];
}

function failureMessage(error: unknown, sent: number, total: number): DymoPrintError {
  const progress =
    sent > 0
      ? ` ${sent} of ${total} labels had already been sent to the printer; check what came out before printing again.`
      : ' Nothing was sent to the printer.';
  if (error instanceof DymoServiceError) {
    if (error.code === 'timeout') {
      return new DymoPrintError(
        'DYMO Connect did not answer in time. Some or all of the labels may still print — check the printer before printing again.',
        { labelsSent: sent, uncertain: true },
      );
    }
    if (error.code === 'unreachable') {
      return new DymoPrintError(`DYMO Connect stopped answering during the print.${progress}`, {
        labelsSent: sent,
      });
    }
    const detail = error.detail ? ` It said: “${error.detail}”.` : '';
    return new DymoPrintError(`DYMO Connect refused the print job.${detail}${progress}`, {
      labelsSent: sent,
    });
  }
  return new DymoPrintError(`The labels could not be sent to the printer.${progress}`, {
    labelsSent: sent,
  });
}

/**
 * Prints the items on the DYMO printer with no dialog: checks the printer once
 * more (it may have been unplugged since the page loaded), builds the job, and
 * hands it over. Throws a {@link DymoPrintError} whose message says what
 * happened and how much was sent; the caller keeps the list and offers the PDF.
 */
export async function printToDymo(
  items: readonly LabelJobItem[],
  env: DymoEnvironment,
  mode: 'label-set' | 'per-label' = DYMO_JOB_MODE,
): Promise<DymoPrintResult> {
  const status = await detectDymo(env);
  const reason = unavailableReason(status);
  if (reason !== null || status.printer === null || status.origin === null) {
    throw new DymoPrintError(`${reason ?? 'The DYMO printer is not available.'} Nothing was sent.`);
  }
  // Belt and braces: a print only ever goes to the local service.
  if (!isDymoServiceOrigin(status.origin)) {
    throw new DymoPrintError('The DYMO printer is not available. Nothing was sent.');
  }

  let job;
  try {
    job = buildDymoJob(items, env.measureTitle, mode);
  } catch (error) {
    throw new DymoPrintError(error instanceof Error ? error.message : 'There is nothing to print.');
  }

  let sent = 0;
  for (const call of job.calls) {
    try {
      await printLabel(
        status.origin,
        {
          printerName: status.printer.name,
          labelXml: call.labelXml,
          labelSetXml: call.labelSetXml,
        },
        env.fetch,
        env.printTimeoutMs,
      );
    } catch (error) {
      throw failureMessage(error, sent, job.labels);
    }
    sent += call.labels;
  }
  return {
    printerName: status.printer.name,
    labels: job.labels,
    jobs: job.calls.length,
    warnings: rollWarnings(status.consumable, job.labels),
  };
}
